import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Card, CardBody, Col, Row, Table } from 'reactstrap';
import { InvoicingDetailDto } from '../../types';
import { invoiceService } from '../../services/InvoiceService';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from './payment-form';
import { PaymentIntent } from '../../types/InvoicingDto';

const currencyFormatter = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: `USD`,
});

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY || ``);

const InvoicePayment = () => {
    const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
    const [invoice, setInvoice] = useState<InvoicingDetailDto>();
    const [selectedProfessions, setSelectedProfessions] = useState<number[]>([]);
    const [payLateFee, setPayLateFee] = useState<boolean>(false);
    const [paymentIntent, setPaymentIntent] = useState<PaymentIntent>();
    const paymentIntentRef = useRef<PaymentIntent>();
    const [paymentAmount, setPaymentAmount] = useState<number>();
    const [email, setEmail] = useState<string>();

    const amountPaid = (inv: InvoicingDetailDto): number => {
        let total = 0;

        if (!!inv.invoicePayments) {
            inv.invoicePayments
                .filter((ip) => !ip.isDeleted)
                .forEach((ip) => {
                    total += +ip.paymentAmount;
                });
        }

        return total;
    };

    const isLateFeeDue = useCallback((inv: InvoicingDetailDto): boolean => {
        let profAmts = 0;
        inv.professionListDeserialized?.forEach((pl) => {
            profAmts += pl.amountPaid || 0;
        });
        return (inv.lateFee || 0) > 0 && amountPaid(inv) <= profAmts;
    }, []);

    useEffect(() => {
        if (!!invoiceNumber) {
            invoiceService
                .getPublicInvoiceByNumber(invoiceNumber)
                .then((response) => {
                    const unpaidProfessions =
                        response.professionListDeserialized?.filter(
                            (pl) => (response?.amount || 0) > (pl.amountPaid || 0),
                        ) || [];
                    setInvoice(response);
                    setSelectedProfessions(unpaidProfessions.map((pl) => pl.id));
                    setPayLateFee(isLateFeeDue(response));
                    setPaymentAmount(response.dueAmount);
                    setEmail(response.recipient.email);
                })
                .catch((_err) => {
                    toast.error(`There was a problem loading the requested invoice.`);
                });
        }
    }, [invoiceNumber, isLateFeeDue]);

    // Amount that will actually be charged: capped at invoice.dueAmount so the
    // charged amount and the recorded AMS payment agree. This same value flows
    // both to Stripe (for the payment intent) and to <PaymentForm> (for the AMS
    // record). Fixes invoice 43652 (IoHS) — the old code sent the uncapped
    // per-profession fee to <PaymentForm>, producing phantom overpayments.
    const amountToCharge = Math.min(paymentAmount || 0, invoice?.dueAmount || 0);

    useEffect(() => {
        // Skip Stripe entirely when there's nothing to charge. Stripe's minimum
        // is $0.50; calling createPaymentIntent with 0 fires a StripeException.
        if (amountToCharge <= 0 || !invoice || !email) return;

        if (!paymentIntentRef.current) {
            invoiceService
                .createPaymentIntent(amountToCharge, email, `Invoice Number: ${invoice.invoiceNumber}`)
                .then((response) => {
                    paymentIntentRef.current = response;
                    setPaymentIntent(response);
                })
                .catch((_err) => {
                    toast.error(`There was an issue setting up the payment form.`);
                });
        } else {
            invoiceService
                .updatePaymentIntent(
                    paymentIntentRef.current.id,
                    amountToCharge,
                    email,
                    `Invoice Number: ${invoice.invoiceNumber}`,
                )
                .then((response) => {
                    paymentIntentRef.current = response;
                    setPaymentIntent(response);
                })
                .catch((_err) => {
                    toast.error(`There was an issue setting up the payment form.`);
                });
        }
    }, [amountToCharge, email, invoice]);

    // Extract complex expressions to separate variables
    const invoiceAmount = invoice?.amount;
    const invoiceLateFee = invoice?.lateFee;

    useEffect(() => {
        if (!!invoiceAmount) {
            let total = selectedProfessions.length * invoiceAmount;
            if (payLateFee && (invoiceLateFee || 0) > 0) {
                total += invoiceLateFee || 0;
            }

            setPaymentAmount(total);
        }
    }, [selectedProfessions, payLateFee, invoiceAmount, invoiceLateFee]);

    const followingYear = (): number | undefined => {
        return !!invoice?.fiscalYear ? +invoice.fiscalYear + 1 : undefined;
    };

    const fiscalYearString = (): string => {
        return !!invoice?.fiscalYear ? `${invoice.fiscalYear} - ${followingYear()}` : ``;
    };

    const formatMoneyOrZero = (amount: number | undefined | null): string => {
        return currencyFormatter.format(amount || 0);
    };

    const amountPaidString = (): string => {
        if (!!invoice) {
            return formatMoneyOrZero(amountPaid(invoice));
        }

        return formatMoneyOrZero(0);
    };

    const hasLateFeeBeenPaid = (): boolean => {
        const total = invoice ? amountPaid(invoice) : 0;
        let profAmts = 0;
        invoice?.professionListDeserialized?.forEach((pl) => {
            profAmts += pl.amountPaid || 0;
        });

        return total > profAmts;
    };

    const stripeOptions = {
        clientSecret: paymentIntent?.clientSecret,
    };

    const getSerializedProfessionIds = () => {
        const profAmounts =
            invoice?.professionListDeserialized
                ?.filter((pl) => selectedProfessions?.includes(pl.id))
                .map((pl) => {
                    return {
                        id: pl.id,
                        amountPaid:
                            pl?.amountPaid === 0
                                ? invoice?.amount || 0
                                : (invoice?.amount || 0) - (pl?.amountPaid || 0),
                    };
                }) || [];
        return JSON.stringify(profAmounts);
    };

    const isProgramPaid = (pl): boolean => {
        if (pl.isPaid === true) {
            return true;
        }

        if ((pl.amountPaid || 0) >= (invoice?.amount || 0)) {
            return true;
        }

        return false;
    };

    return (
        <>
            {!!invoice && (
                <>
                    <Row className="justify-content-center">
                        <Col>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md={9} className={`mb-3`}>
                                            <img
                                                style={{ width: `100%`, maxWidth: `400px` }}
                                                src="https://uploads-ssl.webflow.com/5f466098a462432df91fef63/5fa3462933aa311a701e3196_CAAHEP_Full_LBlue.gif"
                                                alt="CAAHEP Logo"
                                            />
                                        </Col>
                                        <Col md={3} className={`d-flex flex-column mb-3`}>
                                            <div className={`d-flex justify-content-between`}>
                                                <span className={`font-weight-bold`}>Invoice:</span>
                                                <span>{invoice.invoiceNumber}</span>
                                            </div>
                                            <div className={`d-flex justify-content-between`}>
                                                <span className={`font-weight-bold`}>Date:</span>
                                                <span>{moment(invoice.invoiceDate).format(`MM/DD/yyyy`)}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className={`mb-3`}>
                                        {!!invoice.recipient && (
                                            <Col className={`d-flex flex-column`}>
                                                <label>
                                                    <span className={`font-weight-bold`}>To:</span>
                                                </label>
                                                <span>{`${invoice.recipient.firstName} ${invoice.recipient.lastName}`}</span>
                                                <span>{invoice.recipient.title || ``}</span>
                                                <span>{invoice.institutionTitle}</span>
                                                <span>{invoice.recipient.address || ``}</span>
                                                <span>{invoice.recipient.address2 || ``}</span>
                                                <span>{invoice.recipient.address3 || ``}</span>
                                                <span>{invoice.recipient.address4 || ``}</span>
                                                <span>{`${invoice.recipient.city || ``}, ${
                                                    invoice.recipient.state || ``
                                                } ${invoice.recipient.zip || ``}`}</span>
                                            </Col>
                                        )}
                                        <Col className={`d-flex flex-column`}>
                                            <label>
                                                <span className={`font-weight-bold`}>For:</span>
                                            </label>
                                            <span>CAAHEP Accredited Program Fee</span>
                                            {!!invoice.fiscalYear && <span>{fiscalYearString()}</span>}
                                            {!!invoice.fiscalYear && (
                                                <span>{`[July 1, ${
                                                    invoice.fiscalYear
                                                } - June 30, ${followingYear()}]`}</span>
                                            )}
                                        </Col>
                                    </Row>
                                    <Table className={`mb-3`}>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Fee Per Accredited Program</th>
                                                <th>Check the Programs Included in Payment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={3}>
                                                    {`CAAHEP ${fiscalYearString()} Accredited Program Fee`}
                                                </td>
                                            </tr>
                                            {!!invoice.professionListDeserialized &&
                                                invoice.professionListDeserialized.map((pl) => (
                                                    <tr key={pl.id}>
                                                        <td className={`pl-5`}>
                                                            <span>{pl.title}</span>
                                                        </td>
                                                        <td>{formatMoneyOrZero(invoice?.amount)}</td>
                                                        <td>
                                                            {isProgramPaid(pl) === true && (
                                                                <Badge color={'primary'}>Paid</Badge>
                                                            )}
                                                            {!!!isProgramPaid(pl) && (
                                                                <input
                                                                    type={`checkbox`}
                                                                    checked={
                                                                        !!selectedProfessions.find((id) => id === pl.id)
                                                                    }
                                                                    onChange={(e) => {
                                                                        const others = selectedProfessions.filter(
                                                                            (id) => id !== pl.id,
                                                                        );

                                                                        if (e.target.checked) {
                                                                            setSelectedProfessions([...others, pl.id]);
                                                                        } else {
                                                                            setSelectedProfessions([...others]);
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            <tr>
                                                <td>Administrative Fee</td>
                                                <td>{formatMoneyOrZero(invoice.lateFee)}</td>
                                                <td>
                                                    {isLateFeeDue(invoice) && (
                                                        <input
                                                            type={'checkbox'}
                                                            checked={payLateFee}
                                                            onChange={(e) => setPayLateFee(e.target.checked)}
                                                        />
                                                    )}
                                                    {hasLateFeeBeenPaid() && <Badge color={'primary'}>Paid</Badge>}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Amount Paid</td>
                                                <td>{amountPaidString()}</td>
                                                <td></td>
                                            </tr>
                                            <tr>
                                                <td className={`font-weight-bold`}>Amount Due</td>
                                                <td>{formatMoneyOrZero(invoice.dueAmount)}</td>
                                                <td></td>
                                            </tr>
                                            {invoice?.invoicePayments &&
                                                invoice.invoicePayments.map((payment) => (
                                                    <tr key={payment.id}>
                                                        <td>
                                                            <a
                                                                href={payment?.receiptUrl ?? undefined}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                Payment Receipt{' '}
                                                                {moment(payment.paymentDate).format('YYYY-MM-DD')} ($
                                                                {payment.paymentAmount})
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {!!paymentIntent?.clientSecret && !!invoice && (
                        <Row className="justify-content-center">
                            <Col>
                                <Card>
                                    <CardBody>
                                        <Elements stripe={stripePromise} options={stripeOptions}>
                                            <PaymentForm
                                                paymentIntentId={paymentIntent.id}
                                                invoiceId={invoice.id}
                                                amount={amountToCharge}
                                                professionIds={getSerializedProfessionIds()}
                                                recipientEmail={invoice.recipient.email}
                                                onEmailChange={(newEmail) => {
                                                    setEmail(newEmail);
                                                }}
                                            />
                                        </Elements>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </>
    );
};

export default InvoicePayment;
